import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  AccountBalance as BankIcon,
  QrCode2 as QrCodeIcon,
  ReceiptLong as ReceiptIcon,
  Shield as ShieldIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { doc, updateDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';

export default function ClientPaymentModal({
  open,
  onClose,
  invoice,
  onPaymentSubmitted,
  currentUser,
  clientId
}) {
  if (!invoice) return null;

  const currencySymbol = invoice.currency === 'USD' ? '$' : '₹';
  const balanceDue = Number(invoice.balanceDue || 0);

  const bankName = invoice.companyBillingProfile?.bankName || 'IndusInd Bank';
  const accName = invoice.companyBillingProfile?.accountName || 'VERVEAILABS PRIVATE LIMITED';
  const accNum = invoice.companyBillingProfile?.accountNumber || '251401230904';
  const ifsc = invoice.companyBillingProfile?.ifscCode || 'INDB0000866';
  const branch = invoice.companyBillingProfile?.branch || 'Bengaluru';
  const upiId = invoice.companyBillingProfile?.upiId || '251401230904@indus';

  const [copiedField, setCopiedField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    utrNumber: '',
    amount: balanceDue > 0 ? balanceDue : Number(invoice.totalAmount || 0),
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'NEFT / RTGS',
    remittingBank: '',
    notes: ''
  });

  const handleCopy = (fieldKey, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Dynamic UPI Payment Intent Link & QR URL
  const upiPayLink = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(accName)}&am=${encodeURIComponent(formData.amount || balanceDue)}&cu=INR&tn=${encodeURIComponent(`Invoice ${invoice.invoiceNumber}`)}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(upiPayLink)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const trimmedUtr = (formData.utrNumber || '').trim();
    if (!trimmedUtr) {
      setFormError('Please enter the Bank UTR / Transaction Reference Number.');
      return;
    }

    const numericAmount = parseFloat(formData.amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setFormError('Please specify a valid remitted amount.');
      return;
    }

    try {
      setSubmitting(true);

      const submissionPayload = {
        utrNumber: trimmedUtr,
        amount: numericAmount,
        paymentDate: formData.paymentDate,
        paymentMode: formData.paymentMode,
        remittingBank: formData.remittingBank.trim(),
        notes: formData.notes.trim(),
        submittedAt: new Date().toISOString(),
        submittedBy: currentUser?.email || 'client',
        verified: false
      };

      // 1. Update Root Collection `client_invoices/{invoiceId}`
      const invoiceDocRef = doc(db, 'client_invoices', invoice.id);
      await updateDoc(invoiceDocRef, {
        clientUtrSubmission: submissionPayload,
        hasPendingClientUtr: true,
        updatedAt: serverTimestamp()
      });

      // 2. Also mirror to `clients/{clientId}/invoices/{invoiceId}` if clientId available
      const targetClientId = clientId || invoice.clientId;
      if (targetClientId) {
        try {
          const clientSubDocRef = doc(db, 'clients', targetClientId, 'invoices', invoice.id);
          await updateDoc(clientSubDocRef, {
            clientUtrSubmission: submissionPayload,
            hasPendingClientUtr: true,
            updatedAt: serverTimestamp()
          });
        } catch (subErr) {
          console.warn('Subcollection mirror skipped or not found:', subErr);
        }

        // 3. Log into `client_activity_logs`
        try {
          await addDoc(collection(db, 'client_activity_logs'), {
            clientId: targetClientId,
            action: 'CLIENT_PAYMENT_UTR_SUBMITTED',
            details: `Client submitted bank remittance UTR #${trimmedUtr} for Tax Invoice ${invoice.invoiceNumber} (Amount: ₹${numericAmount.toLocaleString('en-IN')})`,
            userEmail: currentUser?.email || 'client-portal',
            timestamp: serverTimestamp()
          });
        } catch (logErr) {
          console.warn('Activity log write error:', logErr);
        }
      }

      if (onPaymentSubmitted) {
        onPaymentSubmitted({
          ...invoice,
          clientUtrSubmission: submissionPayload,
          hasPendingClientUtr: true
        });
      }

      onClose();
    } catch (err) {
      console.error('Error submitting payment remittance:', err);
      setFormError(err.message || 'Failed to submit remittance details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0B0F19',
          color: '#F8FAFC',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.2,
          bgcolor: '#111827',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor: 'rgba(6, 182, 212, 0.15)',
              border: '1px solid rgba(6, 182, 212, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BankIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }}>
              Bank Wire & UPI Remittance
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              Invoice {invoice.invoiceNumber} • Balance Due: <span style={{ color: '#22d3ee', fontWeight: 700 }}>{currencySymbol}{balanceDue.toLocaleString('en-IN')}</span>
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: '#94A3B8', '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 2.5, md: 4 }, bgcolor: '#0B0F19' }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            {formError}
          </Alert>
        )}

        <Grid container spacing={3.5}>
          {/* LEFT COLUMN: Bank Details & Dynamic UPI QR */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <BankIcon sx={{ color: '#06b6d4', fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#22d3ee', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Step 1: Remit via Wire or UPI
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 2 }}>
                Transfer directly to Verve AI Labs Private Limited. Zero processing fees or gateway markups.
              </Typography>

              {/* IndusInd Bank Coordinates */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                {/* Beneficiary */}
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                    Beneficiary Name
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.85rem' }}>
                    {accName}
                  </Typography>
                </Box>

                {/* Account Number with Copy */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(6, 182, 212, 0.06)', border: '1px solid rgba(6, 182, 212, 0.25)' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#06b6d4', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>
                      IndusInd Bank Account No. (Current)
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                      {accNum}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedField === 'acc' ? 'Copied!' : 'Copy Account Number'}>
                    <IconButton size="small" onClick={() => handleCopy('acc', accNum)} sx={{ color: copiedField === 'acc' ? '#10b981' : '#22d3ee' }}>
                      {copiedField === 'acc' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* IFSC Code with Copy */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                      IFSC Code (Branch: {branch})
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace' }}>
                      {ifsc}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedField === 'ifsc' ? 'Copied!' : 'Copy IFSC Code'}>
                    <IconButton size="small" onClick={() => handleCopy('ifsc', ifsc)} sx={{ color: copiedField === 'ifsc' ? '#10b981' : '#94A3B8' }}>
                      {copiedField === 'ifsc' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* UPI Virtual ID with Copy */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '10px', textTransform: 'uppercase' }}>
                      UPI ID (Instant Transfer)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace' }}>
                      {upiId}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedField === 'upi' ? 'Copied!' : 'Copy UPI ID'}>
                    <IconButton size="small" onClick={() => handleCopy('upi', upiId)} sx={{ color: copiedField === 'upi' ? '#10b981' : '#94A3B8' }}>
                      {copiedField === 'upi' ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Dynamic QR Code for Smartphone Scanning */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FFFFFF', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <img
                  src={qrCodeUrl}
                  alt={`UPI QR Code for Invoice ${invoice.invoiceNumber}`}
                  style={{ width: '150px', height: '150px', display: 'block' }}
                />
                <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 700, fontSize: '11px' }}>
                  Scan & Pay with GPay, PhonePe, Paytm, or BHIM
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '10px' }}>
                  Amount auto-populated: {currencySymbol}{Number(formData.amount || balanceDue).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT COLUMN: Confirm Remittance Form (Submit Bank UTR) */}
          <Grid item xs={12} md={6}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <ReceiptIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Step 2: Submit Remittance Reference
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 2.5 }}>
                  After remitting from your corporate bank portal, submit the reference/UTR so our finance team can verify and issue your receipt.
                </Typography>

                <Grid container spacing={2}>
                  {/* UTR Number */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Bank UTR / Transaction Reference #"
                      name="utrNumber"
                      placeholder="e.g. INDB241026889212 or UPI Ref 4299..."
                      value={formData.utrNumber}
                      onChange={handleFormChange}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.4)' },
                          '&:hover fieldset': { borderColor: '#06b6d4' },
                          '&.Mui-focused fieldset': { borderColor: '#22d3ee' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    />
                  </Grid>

                  {/* Remitted Amount */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label={`Remitted Amount (${currencySymbol})`}
                      name="amount"
                      value={formData.amount}
                      onChange={handleFormChange}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    />
                  </Grid>

                  {/* Payment Mode */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Payment Method"
                      name="paymentMode"
                      value={formData.paymentMode}
                      onChange={handleFormChange}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    >
                      <MenuItem value="NEFT / RTGS">NEFT / RTGS</MenuItem>
                      <MenuItem value="IMPS">IMPS Instant Transfer</MenuItem>
                      <MenuItem value="UPI">UPI Remittance</MenuItem>
                      <MenuItem value="Cheque / Direct Deposit">Cheque / Direct Deposit</MenuItem>
                      <MenuItem value="International Wire (SWIFT)">International Wire (SWIFT)</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Payment Date */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Transfer Date"
                      name="paymentDate"
                      value={formData.paymentDate}
                      onChange={handleFormChange}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    />
                  </Grid>

                  {/* Remitting Bank Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Remitting Bank (Optional)"
                      name="remittingBank"
                      placeholder="e.g. HDFC, ICICI, SBI"
                      value={formData.remittingBank}
                      onChange={handleFormChange}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    />
                  </Grid>

                  {/* Remarks */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Remarks / Notes (Optional)"
                      name="notes"
                      placeholder="Any additional transaction notes for accounts reconciliation..."
                      value={formData.notes}
                      onChange={handleFormChange}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                          '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                        },
                        '& .MuiInputLabel-root': { color: '#94A3B8', fontSize: '13px' }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting}
                  startIcon={submitting ? null : <SendIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: 1.2,
                    borderRadius: 2,
                    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #34d399 0%, #047857 100%)' }
                  }}
                >
                  {submitting ? 'Submitting Remittance...' : 'Submit Remittance Details'}
                </Button>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'center', mt: 1, fontSize: '10px' }}>
                  Our accounts team reconciles UTRs against IndusInd Bank statements within 2 to 4 business hours.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
